import { type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Camera/AiAnalytics/AiRuleSettings/AiRuleSettings.less";
import { AspectMaintainer, Button, ButtonTypes, InteractiveSvg } from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { AiAnalyticsState } from "Interfaces";
import type { Point } from "@clintonelec/react-storybook";
import { constant, times } from "lodash";

type AiExclusionProps = {
	activeZoneIndex: number;
	localAiAnalyticsSettings: AiAnalyticsState;
	setActiveZoneIndex: Dispatch<SetStateAction<number>>;
	setLocalAiAnalyticsSettings: Dispatch<SetStateAction<AiAnalyticsState>>;
};

const defaultZonePoints: Point[] = [
	{ x: 760, y: 340 },
	{ x: 1160, y: 340 },
	{ x: 1160, y: 740 },
	{ x: 760, y: 740 }
];

function AiExclusion(props: AiExclusionProps) {
	const { activeZoneIndex, localAiAnalyticsSettings, setActiveZoneIndex, setLocalAiAnalyticsSettings } = props;
	const { exclusionZones, enabled } = localAiAnalyticsSettings;

	const handlePointsChange = (points: Point[]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.exclusionZones[ activeZoneIndex ]) {
				return;
			}

			draft.exclusionZones[ activeZoneIndex ] = points;
		}));
	};

	const handleClearActiveZone = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.exclusionZones[ activeZoneIndex ] = null;
		}));

		setActiveZoneIndex(-1);
	};

	const handleClearAllZones = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.exclusionZones = times(4, (constant(null)));
		}));

		setActiveZoneIndex(-1);
	};

	const handleCreateRuleClick = (index: number) => () => {
		setActiveZoneIndex(index);
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.exclusionZones[ index ] = defaultZonePoints;
		}));
	};

	const handleRuleButtonClick = (index: number) => () => {
		const newIndex = activeZoneIndex === index ? -1 : index;

		setActiveZoneIndex(newIndex);
	};

	const renderRuleButtons = () => times(4, index => {
		if (!exclusionZones[ index ]) {
			return (
				<Button
					key={ index }
					type={ ButtonTypes.SECONDARY }
					ghost={ index !== activeZoneIndex }
					onClick={ handleCreateRuleClick(index) }
					htmlType="button"
					disabled={ !enabled }
				>
					+
				</Button>
			);
		}

		return (
			<Button
				key={ index }
				type={ index !== activeZoneIndex ? ButtonTypes.SECONDARY : ButtonTypes.TERTIARY }
				ghost={ index !== activeZoneIndex }
				onClick={ handleRuleButtonClick(index) }
				htmlType="button"
				disabled={ !enabled }
			>
				{ `Rule ${ index + 1 }` }
			</Button>
		);
	});

	const renderInteractivePolygon = () => {
		const points = exclusionZones[ activeZoneIndex ];

		if (activeZoneIndex < 0 || !enabled || !points) {
			return;
		}

		return (
			<InteractiveSvg.Polygon
				key={ `zone-${ activeZoneIndex }` }
				points={ points }
				onChange={ handlePointsChange }
			/>
		);
	};

	const renderDisabledMessage = () => {
		if (!enabled) {
			return (
				<div className="add-rule-message">Enable AI Analytics to adjust rule settings.</div>
			);
		}

		if (activeZoneIndex < 0 || !exclusionZones[ activeZoneIndex ]) {
			return (
				<div className="add-rule-message">Create an exclusion zone to adjust area.</div>
			);
		}
	};

	return (
		<div className="ai-rule-settings two-column-card-content">
			<div className="column left-content">
				<div className="camera-container">
					<AspectMaintainer>
						<div className="camera-snapshot">
							<InteractiveSvg>
								{ renderInteractivePolygon() }
							</InteractiveSvg>
						</div>
					</AspectMaintainer>
				</div>
				<div className="rule-buttons">
					{ renderRuleButtons() }
				</div>
				<div className="controls-container">
					<Button
						className="rule-action-button"
						disabled={ !enabled || activeZoneIndex === -1 }
						ghost
						htmlType="button"
						onClick={ handleClearActiveZone }
						type={ ButtonTypes.SECONDARY }
					>
						Clear Active Rule
					</Button>
					<Button
						className="rule-action-button"
						disabled={ !enabled || !exclusionZones.some((pointsArray) => !!pointsArray) }
						type={ ButtonTypes.SECONDARY }
						ghost
						htmlType="button"
						onClick={ handleClearAllZones }
					>
						Clear All Rules
					</Button>
				</div>
			</div>
			<div className="column right-content">
				{ renderDisabledMessage() }
			</div>
		</div>
	);
}

export default memo(AiExclusion);
